import './polyfills';

import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Toaster } from 'sonner';
import { ErrorBoundary } from 'react-error-boundary';
import { TonConnectUIProvider } from '@tonconnect/ui-react';

import { log } from '@app/shared/utils/logger';

const getTelegramStartupSnapshot = () => {
  const tg = window.Telegram?.WebApp;
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const searchParams = new URLSearchParams(window.location.search);

  return {
    hasTelegramObject: typeof window.Telegram !== 'undefined',
    hasTelegramWebApp: !!tg,
    initDataLength: tg?.initData?.length ?? 0,
    hasInitDataUnsafeUser: !!tg?.initDataUnsafe?.user,
    platform: tg?.platform,
    version: tg?.version,
    isExpanded: tg?.isExpanded,
    colorScheme: tg?.colorScheme,
    hashKeys: Array.from(hashParams.keys()),
    searchKeys: Array.from(searchParams.keys()),
    hasHashTgWebAppData: hashParams.has('tgWebAppData'),
    hasSearchTgWebAppData: searchParams.has('tgWebAppData'),
    hasStartParam: hashParams.has('tgWebAppStartParam') || searchParams.has('tgWebAppStartParam'),
    referrer: document.referrer,
    readyState: document.readyState,
  };
};

void log({
  level: 'info',
  message: 'appStarted',
  extra: getTelegramStartupSnapshot(),
});

window.addEventListener('DOMContentLoaded', () => {
  void log({
    level: 'info',
    message: 'appDomContentLoaded',
    extra: getTelegramStartupSnapshot(),
  });
});

window.addEventListener('load', () => {
  void log({
    level: 'info',
    message: 'appWindowLoaded',
    extra: getTelegramStartupSnapshot(),
  });
});

window.addEventListener('error', (error) => {
  void log({
    level: 'error',
    message: 'appWindowError',
    error: error.error ?? error.message,
    extra: {
      ...getTelegramStartupSnapshot(),
      message: error.message,
      filename: error.filename,
      lineno: error.lineno,
      colno: error.colno,
    },
  });
});
window.addEventListener('unhandledrejection', (error) => {
  void log({
    level: 'error',
    message: 'appUnhandledRejection',
    error: error.reason,
    extra: getTelegramStartupSnapshot(),
  });
});

import { TelegramProvider, AuthProvider } from '@app/shared/providers';
import AppRouter from '@app/routes/AppRouter';
import { ErrorFallback } from '@app/shared/ui/organisms/Common';
import { LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { store } from '@app/store/store';
import { TONCONNECT_MANIFEST_URL } from '@app/shared/constants/api';
import './app/i18n/i18n';

import '@app/styles/global.css';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <TonConnectUIProvider manifestUrl={TONCONNECT_MANIFEST_URL}>
          <ErrorBoundary
            fallback={<ErrorFallback />}
            onError={(error, info) => log({ level: 'error', error, extra: { info } })}
          >
            <Suspense fallback={<LoadingSpinner mode="overlay" size="lg" />}>
              <TelegramProvider>
                <AuthProvider>
                  <AppRouter />
                </AuthProvider>
              </TelegramProvider>
            </Suspense>
          </ErrorBoundary>
          <Toaster
            position="top-center"
            richColors
            closeButton
            visibleToasts={1}
            toastOptions={{
              classNames: {
                toast: 'rounded-2xl!',
              },
            }}
            offset={{
              top: 'calc(32px + var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px))',
            }}
            mobileOffset={{
              top: 'calc(16px + var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px))',
            }}
          />
        </TonConnectUIProvider>
      </BrowserRouter>
    </Provider>
  </StrictMode>
);
